import type {CodeSectionLookup} from '@github-ui/code-nav'
import {clsx} from 'clsx'

import {useCurrentLineHeight} from '../../../../hooks/use-current-line-height'
import {type SharedLineObserverHandle, useRegisterLineObserver} from '../../../../hooks/use-shared-line-observer'
import {getActualLineNumberBinarySearch} from '../../../../utilities/lines'
import type {CodeLineData} from './hooks/use-code-lines'
import styles from './StickyLineObserverOverlay.module.css'

export function StickyLineObserverOverlay({
  linesData,
  sharedLineObserver,
  codeLineToSectionMap,
}: {
  linesData: CodeLineData[]
  sharedLineObserver?: SharedLineObserverHandle
  codeLineToSectionMap?: CodeSectionLookup
}) {
  const lineHeight = useCurrentLineHeight('react-line-numbers')
  if (codeLineToSectionMap === undefined) return null

  return (
    <>
      {linesData.map(lineData => {
        const lineNumber = lineData.lineNumber
        const numParents = codeLineToSectionMap?.get(lineNumber)?.length ?? 0
        if (lineNumber === undefined || lineData.ownedSection === undefined) return null
        const actualLineNumber = getActualLineNumberBinarySearch(lineNumber, linesData)

        return (
          <StickyLineObserverLine
            key={`observer-overlay-${actualLineNumber}-lineNumber-no-virtualization`}
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            className="symbol-highlight react-code-text"
            lineData={linesData[actualLineNumber]!}
            sharedLineObserver={sharedLineObserver}
            numParents={numParents}
            style={{
              top: lineHeight * (actualLineNumber - 1),
              height: lineHeight,
            }}
          />
        )
      })}
    </>
  )
}

function StickyLineObserverLine({
  lineData,
  sharedLineObserver,
  numParents,
  className,
  style,
}: {
  lineData: CodeLineData
  sharedLineObserver?: SharedLineObserverHandle
  numParents: number
  className: string
  style: React.CSSProperties
}) {
  const refCallback = useRegisterLineObserver(sharedLineObserver, lineData, numParents)
  return (
    <div
      ref={ref => {
        refCallback(ref)
      }}
      className={clsx(className, styles.stickyLineObserver)}
      style={style}
      data-testid={'sticky-line-observer'}
    />
  )
}
